import { useState, useRef } from "react";
import styled, { useTheme } from "styled-components";
import { Icon, IconKeys, localStorage } from "dash-ui";
import { useTranslation } from "react-i18next";

import { useQuickTour } from "common/components/QuickTour";

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 180;

const SidebarOuter = styled.div<{ $expanded: boolean }>`
  position: fixed;
  left: 0;
  top: 50px;
  bottom: 0;
  z-index: 10;
  width: ${({ $expanded }) => ($expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
  transition: width 0.2s ease-in-out;

  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.mobile}`} {
    display: none;
  }
`;

const SidebarWrapper = styled.div<{ $expanded: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $expanded }) => ($expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
  background-color: #e6eef4;
  transition: width 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Spacer = styled.div<{ $expanded: boolean; $pinned: boolean }>`
  flex-shrink: 0;
  width: ${({ $pinned, $expanded }) =>
    $pinned && $expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}px;
  transition: width 0.2s ease-in-out;

  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.mobile}`} {
    display: none;
  }
`;

const InnerContainer = styled.div`
  width: ${EXPANDED_WIDTH}px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 16px 12px;
  margin-bottom: 20px;

  svg {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
  }
`;

const MenuLabel = styled.span<{ $visible: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gr2};
  white-space: nowrap;
  margin-left: 12px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  gap: 16px;
`;

const NavItem = styled.button<{ $isActive: boolean; $expanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  cursor: pointer;
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.b50 : "#D5E1EA"};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.white : theme.colors.gr1};
  border-radius: 8px;
  padding: 10px;
  height: 40px;
  width: ${({ $expanded }) => ($expanded ? "100%" : "40px")};
  white-space: nowrap;
  transition: background-color 0.15s ease-in-out, width 0.2s ease-in-out;

  &:hover {
    background: ${({ $isActive, theme }) =>
      $isActive ? theme.colors.b50Hover : "#C8D6E1"};
  }

  svg {
    flex-shrink: 0;
  }
`;

const NavLabel = styled.span<{ $visible: boolean }>`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;

const PinButton = styled.button<{ $expanded: boolean }>`
  position: absolute;
  bottom: 24px;
  right: -14px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.n30};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 11;

  &:hover {
    background: ${({ theme }) => theme.colors.b30};
  }
`;

export interface CollapsibleSideBarItem {
  label: string;
  icon?: IconKeys;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

interface CollapsibleSideBarProps {
  items: CollapsibleSideBarItem[];
}

function CollapsibleSideBar({ items }: CollapsibleSideBarProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { activeStepKey } = useQuickTour();
  const [pinned, setPinned] = useState(() => localStorage.getSidebarPinned());
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const expanded = pinned || hovered || activeStepKey === "sidebar";

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
    }, 100);
  };

  const handlePinToggle = () => {
    setPinned((prev) => {
      const next = !prev;
      localStorage.setSidebarPinned(next);
      return next;
    });
  };

  return (
    <>
      <Spacer $expanded={expanded} $pinned={pinned} />
      <SidebarOuter
        data-tour="sidebar"
        $expanded={expanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <SidebarWrapper $expanded={expanded}>
          <InnerContainer>
            <MenuHeader>
              <Icon name="menu" size={32} color="currentColor" />
              <MenuLabel $visible={expanded}>{t("labels.menu")}</MenuLabel>
            </MenuHeader>

            <NavList>
              {items.map(({ icon, label, onClick, isActive, disabled }) => (
                <NavItem
                  key={label}
                  $isActive={!!isActive}
                  $expanded={expanded}
                  onClick={onClick}
                  disabled={disabled}
                >
                  {icon && (
                    <Icon
                      name={icon}
                      size={icon === "search" ? 24 : 20}
                      color={isActive ? "white" : theme.colors.gr1}
                      ml={icon === "search" ? -0.5 : undefined}
                    />
                  )}
                  <NavLabel $visible={expanded}>{label}</NavLabel>
                </NavItem>
              ))}
            </NavList>
          </InnerContainer>
        </SidebarWrapper>

        <PinButton $expanded={expanded} onClick={handlePinToggle}>
          <Icon
            name={pinned ? "chevronLeft" : "chevronRight"}
            size={16}
            color="currentColor"
          />
        </PinButton>
      </SidebarOuter>
    </>
  );
}

export default CollapsibleSideBar;
